import {
  DataTable,
  OverflowMenu,
  OverflowMenuItem,
  OverflowMenuItemProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import React from "react";

import TableEmptyState from "src/components/DataStates/EmptyTableState.tsx";
import Loading from "src/components/DataStates/Loading.tsx";
import TextError from "src/components/Errors/TextError.tsx";
import { SortingOrder } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { hasData, isEmpty } from "src/utils/arrays.utils.ts";

// type is not complete
export type OverflowMenuItemType<T> = {
  onClick: (item: T) => void;
} & OverflowMenuItemProps;

export type AppTableHeader = { key: string; header: string; isSortable?: boolean };

type AppDataTableProps<T, TSortField> = {
  rows: T[];
  data: T[];
  headers: AppTableHeader[];
  error: string | null | undefined;
  loading: boolean | null | undefined;
  overflowMenuItems?: (row: T) => OverflowMenuItemType<T>[];
  withFilters?: boolean; // if any filter is activated
  onClickRow?: (id: string) => void;
  selectRow?: (id: string) => void;
  selectAll?: () => void;
  onHeaderClick?: (key: TSortField) => void;
  getOrderByKey?: (key: TSortField) => SortingOrder | null;
  isSortHeader?: (key: TSortField) => boolean;
};

// Stop propagation if text is selected and return true, return false otherwise
const handleTextSelection = (event: React.MouseEvent): boolean => {
  const selection = window?.getSelection()?.toString();
  if (selection) {
    event.stopPropagation();
    return true;
  }
  return false;
};

function DataTableBody<T extends { id: string }, TSortField>({
  rows,
  headers,
  loading,
  error,
  data,
  overflowMenuItems,
  getRowProps,
  onClickRow,
  withFilters,
  selectRow,
  selectAll,
}: AppDataTableProps<T, TSortField> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowProps: any;
}) {
  const RowWrapper = ({ children }: { children: React.ReactNode }) => (
    <TableBody>
      <TableRow {...getRowProps}>
        <TableCell colSpan={headers.length + 1}>{children}</TableCell>
      </TableRow>
    </TableBody>
  );

  if (error) {
    return (
      <RowWrapper>
        <TextError message={error} />
      </RowWrapper>
    );
  }
  if (loading) {
    return (
      <RowWrapper>
        <Loading />
      </RowWrapper>
    );
  }
  if (withFilters && isEmpty(data)) {
    return (
      <RowWrapper>
        <TableEmptyState isFilterResult={true} />
      </RowWrapper>
    );
  }
  if (isEmpty(data)) {
    return (
      <RowWrapper>
        <TableEmptyState />
      </RowWrapper>
    );
  }
  return (
    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          {...getRowProps({ row })}
          onClick={(evt) => {
            if (handleTextSelection(evt)) return;
            // handle selection only if the row is clickable
            if (onClickRow) {
              selectAll?.();
              selectRow?.(row.id);
            }
          }}
        >
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row as any).cells.map((cell) => (
              <TableCell
                onClick={(evt) => {
                  if (handleTextSelection(evt)) return;
                  return onClickRow?.(row.id);
                }}
                key={cell.id}
              >
                {cell.value}
              </TableCell>
            ))
          }
          {hasData(overflowMenuItems?.(row)) && (
            <TableCell>
              <OverflowMenu flipped={true}>
                {overflowMenuItems?.(row).map((item) => (
                  <OverflowMenuItem
                    key={item.id}
                    itemText={<div>{item.itemText}</div>}
                    onClick={() => item.onClick(row)}
                    disabled={item.disabled}
                    isDelete={item.isDelete}
                    hasDivider={item.hasDivider}
                  />
                ))}
              </OverflowMenu>
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
}

function AppDataTable<T extends { id: string }, TSortField = null>({
  rows,
  headers,
  loading,
  error,
  data,
  overflowMenuItems,
  onClickRow,
  withFilters,
  onHeaderClick,
  getOrderByKey,
  isSortHeader,
}: AppDataTableProps<T, TSortField>) {
  return (
    <DataTable rows={rows} headers={headers}>
      {({ rows, headers, getTableProps, getHeaderProps, getRowProps, selectRow, selectAll }) => {
        return (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader
                    {...getHeaderProps({ header })}
                    key={header.key}
                    onClick={() => onHeaderClick?.(header.key as TSortField)}
                    isSortHeader={isSortHeader?.(header.key as TSortField) ?? false}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    isSortable={(header as any).isSortable}
                    sortDirection={getOrderByKey?.(header.key as TSortField) ?? "NONE"}
                  >
                    {header.header}
                  </TableHeader>
                ))}
                {hasData(overflowMenuItems?.(rows[0] as unknown as T)) && (
                  <TableHeader></TableHeader>
                )}
              </TableRow>
            </TableHead>
            <DataTableBody<T, TSortField>
              data={data}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              headers={headers as any}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              rows={rows as any}
              getRowProps={getRowProps}
              selectRow={selectRow}
              selectAll={selectAll}
              loading={loading}
              error={error}
              overflowMenuItems={overflowMenuItems}
              onClickRow={onClickRow}
              withFilters={withFilters}
            />
          </Table>
        );
      }}
    </DataTable>
  );
}

export default AppDataTable;
